/**
 * ЕДИНЫЙ КОНТРАКТ конфигурации виджета.
 *
 * Модель шаблонов (как темы сайтов в CMS):
 *   • ШАБЛОН — самостоятельный виджет: свой скелет (структура+логика) + свой CSS.
 *     Живёт в @inbx/widget-render (render + css). Здесь — только МЕТА шаблона
 *     (id/type/name/description/defaultContent) для галереи редактора, сида и API.
 *   • ТИП (chat/form/survey/promo) — группа шаблонов. Определяет НАСТРОЙКИ и
 *     ТРИГГЕРЫ (контент chat ≠ form ≠ survey…). Любой шаблон своего типа умеет
 *     отрисовать конфиг этого типа.
 *   • Поверх выбранного шаблона — ПЕРЕМЕННЫЕ пользователя (--inbx-*) из редактора
 *     и/или его custom CSS.
 *
 * Хранение: theme_config={template_id,vars} / content_config / behavior_config /
 * media_config (jsonb). Старый design_config поддержан адаптером (legacy→v2).
 */
import { z } from 'zod';

import { widgetTypeSchema } from './widget.schema';

export type WidgetType = z.infer<typeof widgetTypeSchema>;

// ─────────────────────────── THEME (выбор шаблона + переменные) ──────────────
export const themeConfigSchema = z
  .object({
    /** id выбранного шаблона (см. WIDGET_TEMPLATE_META / @inbx/widget-render). */
    template_id: z.string().optional(),
    /** Переопределения CSS-переменных (--inbx-*): цвет, шрифт, размеры, радиус… */
    vars: z.record(z.string()).optional(),
  })
  .passthrough();
export type ThemeConfig = z.infer<typeof themeConfigSchema>;

// ─────────────────────────── CONTENT (по типам) ─────────────────────────────
export const widgetFormFieldSchema = z
  .object({
    key: z.string(),
    type: z.enum(['text', 'email', 'phone', 'textarea', 'select', 'radio', 'checkbox', 'rating', 'file']),
    label: z.string(),
    required: z.boolean().optional(),
    placeholder: z.string().optional(),
    options: z.array(z.union([z.string(), z.object({ value: z.string(), label: z.string() })])).optional(),
    hint: z.string().optional(),
  })
  .passthrough();
export type WidgetFormField = z.infer<typeof widgetFormFieldSchema>;

export const chatContentSchema = z
  .object({
    title: z.string().optional(),
    greeting: z.string().optional(),
    placeholder: z.string().optional(),
    offline_message: z.string().optional(),
    status_text: z.string().optional(),
    teaser_text: z.string().optional(),
    show_agent_avatar: z.boolean().optional(),
    avatar_url: z.string().optional(),
    enable_sound: z.boolean().optional(),
    quick_replies: z.array(z.string()).optional(),
  })
  .passthrough();
export type ChatContent = z.infer<typeof chatContentSchema>;

export const formContentSchema = z
  .object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    fields: z.array(widgetFormFieldSchema).default([]),
    submit_label: z.string().optional(),
    success_message: z.string().optional(),
    reply_via: z.object({ field_key: z.string(), channel: z.enum(['email', 'phone']) }).optional(),
  })
  .passthrough();
export type FormContent = z.infer<typeof formContentSchema>;

export const surveyQuestionSchema = z
  .object({
    id: z.string(),
    text: z.string(),
    description: z.string().optional(),
    required: z.boolean().optional(),
    type: z.enum([
      'open_text',
      'single_choice',
      'multiple_choice',
      'rating',
      'scale',
      'email',
      'phone',
      'image_choice',
    ]),
    options: z.array(z.string()).optional(),
    /** Варианты-картинки (для type=image_choice): «выберите стиль» и т.п. */
    image_options: z
      .array(z.object({ value: z.string(), image_url: z.string(), label: z.string().optional() }))
      .optional(),
    has_other: z.boolean().optional(),
    rating_type: z.enum(['stars', 'nps', 'emoji']).optional(),
    rating_min_label: z.string().optional(),
    rating_max_label: z.string().optional(),
    scale_min: z.number().optional(),
    scale_max: z.number().optional(),
    scale_step: z.number().optional(),
    scale_min_label: z.string().optional(),
    scale_max_label: z.string().optional(),
    /** Ветвления: при совпадении ответа перейти к вопросу (id) или завершить ('__end__'). */
    branches: z
      .array(z.object({ when: z.object({ answer: z.string() }), goto: z.string() }))
      .optional(),
  })
  .passthrough();
export type SurveyQuestion = z.infer<typeof surveyQuestionSchema>;

export const surveyContentSchema = z
  .object({
    title: z.string().optional(),
    questions: z.array(surveyQuestionSchema).default([]),
    welcome_screen: z.boolean().optional(),
    welcome_text: z.string().optional(),
    completion_message: z.string().optional(),
    show_progress: z.boolean().optional(),
    allow_back: z.boolean().optional(),
    reply_via: z.object({ field_key: z.string(), channel: z.enum(['email', 'phone']) }).optional(),
    after_submit_create_ticket: z.boolean().optional(),
    after_submit_tag: z.string().optional(),
    after_submit_webhook: z.string().optional(),
  })
  .passthrough();
export type SurveyContent = z.infer<typeof surveyContentSchema>;

export const promoContentSchema = z
  .object({
    headline: z.string().optional(),
    body_text: z.string().optional(),
    cta_label: z.string().optional(),
    cta_url: z.string().optional(),
    dismissible: z.boolean().optional(),
  })
  .passthrough();
export type PromoContent = z.infer<typeof promoContentSchema>;

// ─────────────────────────── BEHAVIOR / MEDIA ───────────────────────────────
export const widgetDisplayModeSchema = z.enum(['popup', 'inline', 'modal']);
export type WidgetDisplayMode = z.infer<typeof widgetDisplayModeSchema>;
export const widgetPositionSchema = z.enum(['bottom-right', 'bottom-left', 'top-right', 'top-left']);
export type WidgetPosition = z.infer<typeof widgetPositionSchema>;

export const behaviorConfigSchema = z
  .object({
    mode: widgetDisplayModeSchema.default('popup'),
    position: widgetPositionSchema.default('bottom-right'),
    auto_open_after: z.number().int().min(0).default(0),
    show_once_per: z.enum(['session', 'day', 'week', 'forever']).default('session'),
    dismissible: z.boolean().default(true),
    scenario_id: z.string().nullable().optional(),
  })
  .passthrough();
export type BehaviorConfig = z.infer<typeof behaviorConfigSchema>;

export const mediaConfigSchema = z
  .object({
    image_url: z.string().optional(),
    image_alt: z.string().optional(),
    image_position: z.enum(['left', 'right', 'top', 'bg']).optional(),
  })
  .passthrough();
export type MediaConfig = z.infer<typeof mediaConfigSchema>;

// ─────────────────── Плоский конфиг для рендерера ───────────────────────────
export interface RuntimeWidgetConfig {
  widgetId: string;
  publicKey: string;
  workspaceId?: string;
  type: WidgetType;
  /** id выбранного шаблона (резолвится в render+css в @inbx/widget-render). */
  templateId: string;
  /** Переменные пользователя (--inbx-*) — накладываются поверх css шаблона. */
  vars: Record<string, string>;
  content: ChatContent | FormContent | SurveyContent | PromoContent;
  behavior: BehaviorConfig;
  media: MediaConfig;
  customCssUrl: string | null;
  botScenarioId: string | null;
}

// ─────────────────── МЕТА шаблонов (для галереи/сида/API) ───────────────────
export interface WidgetTemplateMeta {
  id: string;
  type: WidgetType;
  name: string;
  description: string;
  /** Стартовый контент при создании виджета из этого шаблона. */
  defaultContent: Record<string, unknown>;
  defaultBehavior?: Partial<BehaviorConfig>;
}

const CHAT_DEFAULT = {
  title: 'Чат с поддержкой',
  greeting: 'Здравствуйте! Чем можем помочь?',
  placeholder: 'Введите сообщение',
  offline_message: 'Сейчас мы офлайн. Оставьте email — ответим на почту.',
  status_text: 'Операторы онлайн',
  show_agent_avatar: true,
};
const FORM_DEFAULT = {
  title: 'Оставьте заявку',
  subtitle: 'Перезвоним в течение 15 минут',
  submit_label: 'Отправить',
  success_message: 'Спасибо! Скоро свяжемся с вами.',
  reply_via: { field_key: 'email', channel: 'email' as const },
  fields: [
    { key: 'name', type: 'text', label: 'Имя', required: true },
    { key: 'email', type: 'email', label: 'Email', required: true },
    { key: 'message', type: 'textarea', label: 'Сообщение' },
  ],
};
const SURVEY_DEFAULT = {
  title: 'Опрос',
  show_progress: true,
  allow_back: true,
  completion_message: 'Спасибо за ответы!',
  questions: [
    { id: 'q1', text: 'Насколько вы довольны сервисом?', type: 'rating', rating_type: 'stars', required: true },
    { id: 'q2', text: 'Что можно улучшить?', type: 'open_text' },
  ],
};
const SURVEY_NPS_DEFAULT = {
  title: 'NPS',
  show_progress: false,
  completion_message: 'Спасибо за оценку!',
  questions: [
    { id: 'nps', text: 'Насколько вероятно, что вы порекомендуете нас?', type: 'rating', rating_type: 'nps', rating_min_label: 'Точно нет', rating_max_label: 'Точно да', required: true },
    { id: 'why', text: 'Почему вы поставили такую оценку?', type: 'open_text' },
  ],
};
const PROMO_DEFAULT = {
  headline: 'Не уходите!',
  body_text: 'Подпишитесь и получите скидку 10% на первый заказ.',
  cta_label: 'Получить скидку',
  cta_url: '#',
  dismissible: true,
};

/** Каталог шаблонов (мета). render+css — в @inbx/widget-render по тому же id. */
export const WIDGET_TEMPLATE_META: WidgetTemplateMeta[] = [
  { id: 'chat-support', type: 'chat', name: 'Поддержка', description: 'Тёмный хедер, кнопка-звонок, быстрые ответы, тулбар', defaultContent: CHAT_DEFAULT, defaultBehavior: { mode: 'popup', position: 'bottom-right' } },
  { id: 'chat-minimal', type: 'chat', name: 'Минимал', description: 'Лаконичный светлый чат без лишнего', defaultContent: { ...CHAT_DEFAULT, show_agent_avatar: false }, defaultBehavior: { mode: 'popup', position: 'bottom-right' } },
  { id: 'chat-spacious', type: 'chat', name: 'Просторный', description: 'Крупный градиентный хедер-приветствие, мессенджер-стиль', defaultContent: { ...CHAT_DEFAULT, title: 'Чем помочь?' }, defaultBehavior: { mode: 'popup', position: 'bottom-right' } },

  { id: 'form-card', type: 'form', name: 'Карточка', description: 'Все поля на одном экране', defaultContent: FORM_DEFAULT, defaultBehavior: { mode: 'inline' } },
  { id: 'form-stepper', type: 'form', name: 'Пошаговая', description: 'Один вопрос на экран, крупная типографика, прогресс', defaultContent: { ...FORM_DEFAULT, subtitle: undefined }, defaultBehavior: { mode: 'inline' } },
  { id: 'form-callback', type: 'form', name: 'Обратный звонок', description: 'Компактная форма: имя и телефон, «перезвоним»', defaultContent: { title: 'Заказать звонок', subtitle: 'Перезвоним за 30 секунд', submit_label: 'Жду звонка', success_message: 'Спасибо! Скоро перезвоним.', reply_via: { field_key: 'phone', channel: 'phone' as const }, fields: [{ key: 'name', type: 'text', label: 'Имя' }, { key: 'phone', type: 'phone', label: 'Телефон', required: true, placeholder: '+7 (___) ___-__-__' }] }, defaultBehavior: { mode: 'popup', position: 'bottom-right' } },

  { id: 'survey-card', type: 'survey', name: 'Карточка', description: 'Вопросы по одному с прогрессом и «назад»', defaultContent: SURVEY_DEFAULT, defaultBehavior: { mode: 'inline' } },
  { id: 'survey-fullscreen', type: 'survey', name: 'На весь экран', description: 'Один вопрос крупно по центру, большие кнопки', defaultContent: SURVEY_DEFAULT, defaultBehavior: { mode: 'inline' } },
  { id: 'survey-nps', type: 'survey', name: 'NPS', description: 'Одна шкала 0–10 + комментарий', defaultContent: SURVEY_NPS_DEFAULT, defaultBehavior: { mode: 'inline' } },

  { id: 'promo-modal', type: 'promo', name: 'Модалка', description: 'Центрированное окно с затемнением', defaultContent: PROMO_DEFAULT, defaultBehavior: { mode: 'modal', show_once_per: 'day' } },
  { id: 'promo-banner', type: 'promo', name: 'Полоса', description: 'Горизонтальная полоса сверху/снизу', defaultContent: { ...PROMO_DEFAULT, headline: 'Бесплатная доставка от 3000 ₽', cta_label: 'В каталог' }, defaultBehavior: { mode: 'inline' } },
  { id: 'promo-corner', type: 'promo', name: 'Уголок', description: 'Компактная карточка в углу', defaultContent: { ...PROMO_DEFAULT, headline: 'Новинки сезона', cta_label: 'Смотреть' }, defaultBehavior: { mode: 'popup', position: 'bottom-right' } },
];

export function templateMetaByType(type: WidgetType): WidgetTemplateMeta[] {
  return WIDGET_TEMPLATE_META.filter((t) => t.type === type);
}
export function findTemplateMeta(id: string): WidgetTemplateMeta | undefined {
  return WIDGET_TEMPLATE_META.find((t) => t.id === id);
}
export function defaultTemplateMeta(type: WidgetType): WidgetTemplateMeta {
  return templateMetaByType(type)[0]!;
}

// ──────────────────────── АДАПТЕРЫ ─────────────────────────────────────────
export interface WidgetConfigSource {
  widgetId: string;
  publicKey: string;
  workspaceId?: string;
  type: WidgetType;
  themeConfig?: Record<string, unknown> | null;
  contentConfig?: Record<string, unknown> | null;
  behaviorConfig?: Record<string, unknown> | null;
  mediaConfig?: Record<string, unknown> | null;
  designConfig?: Record<string, unknown> | null;
  customCssUrl?: string | null;
  botScenarioId?: string | null;
}

function isEmpty(o: Record<string, unknown> | null | undefined): boolean {
  return !o || Object.keys(o).length === 0;
}

/** legacy design_config → content (минимально, для старых виджетов). */
function legacyContent(type: WidgetType, dc: Record<string, unknown>): Record<string, unknown> {
  if (type === 'chat')
    return { title: dc['title'], greeting: dc['welcome_message'], placeholder: dc['placeholder'], offline_message: dc['offline_message'] };
  if (type === 'form')
    return { fields: dc['fields'] ?? [], submit_label: dc['submit_label'], success_message: dc['success_message'], reply_via: dc['reply_via'] };
  if (type === 'survey')
    return { questions: dc['questions'] ?? [], completion_message: dc['completion_message'], show_progress: dc['show_progress'], allow_back: dc['allow_back'], reply_via: dc['reply_via'] };
  return { headline: dc['headline'], body_text: dc['body_text'], cta_label: dc['cta_label'], cta_url: dc['cta_url'] };
}

/** Строит плоский RuntimeWidgetConfig. zod не используется (вызывается и в рантайме). */
export function resolveRuntimeConfig(src: WidgetConfigSource): RuntimeWidgetConfig {
  const type = src.type;
  const themeRaw = (src.themeConfig ?? {}) as { template_id?: string; vars?: Record<string, string> };
  let content = (src.contentConfig ?? {}) as Record<string, unknown>;
  const behaviorRaw = (src.behaviorConfig ?? {}) as Record<string, unknown>;
  const mediaRaw = (src.mediaConfig ?? {}) as Record<string, unknown>;

  if (isEmpty(content) && !isEmpty(src.designConfig)) {
    content = legacyContent(type, src.designConfig as Record<string, unknown>);
  }

  const templateId = themeRaw.template_id || defaultTemplateMeta(type).id;
  const vars = themeRaw.vars ?? {};
  const behavior: BehaviorConfig = {
    ...behaviorRaw,
    mode: (behaviorRaw['mode'] as BehaviorConfig['mode']) ?? 'popup',
    position: (behaviorRaw['position'] as BehaviorConfig['position']) ?? 'bottom-right',
    auto_open_after: (behaviorRaw['auto_open_after'] as number) ?? 0,
    show_once_per: (behaviorRaw['show_once_per'] as BehaviorConfig['show_once_per']) ?? 'session',
    dismissible: (behaviorRaw['dismissible'] as boolean) ?? true,
    scenario_id: (behaviorRaw['scenario_id'] as string | null | undefined) ?? null,
  };

  return {
    widgetId: src.widgetId,
    publicKey: src.publicKey,
    workspaceId: src.workspaceId,
    type,
    templateId,
    vars,
    content: content as RuntimeWidgetConfig['content'],
    behavior,
    media: { ...mediaRaw } as MediaConfig,
    customCssUrl: src.customCssUrl ?? null,
    botScenarioId: src.botScenarioId ?? behavior.scenario_id ?? null,
  };
}
